// src/lib/confirmToast.js

import toast from 'react-hot-toast';
import { FiAlertTriangle, FiTrash2 } from 'react-icons/fi';

/**
 * Remplace window.confirm() par un toast élégant.
 * Retourne une Promise<boolean>.
 *
 * @param {Object} options
 * @param {string} options.title       - Titre principal (ex: "Supprimer ce document ?")
 * @param {string} [options.subtitle]  - Ligne secondaire optionnelle (ex: "Cette action est irréversible.")
 * @param {string} [options.confirmLabel] - Label bouton OK (défaut : "Supprimer")
 * @param {string} [options.cancelLabel]  - Label bouton Annuler (défaut : "Annuler")
 * @param {'danger'|'warning'} [options.variant] - Couleur de l'icône (défaut : 'danger')
 */
export const confirmToast = ({
  title,
  subtitle,
  confirmLabel = 'Supprimer',
  cancelLabel  = 'Annuler',
  variant      = 'danger',
}) =>
  new Promise((resolve) => {
    toast(
      (t) => (
        <div className="flex flex-col gap-3 min-w-[240px] max-w-[320px]">
          {/* Icône + texte */}
          <div className="flex items-start gap-2.5">
            <div
              className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                variant === 'danger'
                  ? 'bg-red-100 text-red-600'
                  : 'bg-orange-100 text-orange-600'
              }`}
            >
              {variant === 'danger'
                ? <FiTrash2 className="w-4 h-4" />
                : <FiAlertTriangle className="w-4 h-4" />}
            </div>
            <div className="flex flex-col gap-0.5">
              <p className="text-sm font-semibold text-gray-800 leading-snug">
                {title}
              </p>
              {subtitle && (
                <p className="text-xs text-gray-500 leading-snug">
                  {subtitle}
                </p>
              )}
            </div>
          </div>

          {/* Boutons */}
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={() => {
                toast.dismiss(t.id);
                resolve(false);
              }}
              className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              {cancelLabel}
            </button>
            <button
              onClick={() => {
                toast.dismiss(t.id);
                resolve(true);
              }}
              className={`px-3 py-1.5 text-xs font-semibold text-white rounded-lg transition-colors ${
                variant === 'danger'
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-orange-600 hover:bg-orange-700'
              }`}
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      ),
      {
        duration: Infinity,   // reste affiché jusqu'au clic
        style: {
          padding: '14px 16px',
          borderRadius: '12px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          maxWidth: '360px',
        },
      }
    );
  });
